import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector } from '@angular/core';

import { CONFIG_MODAL_INPUTS_TOKEN, ModalOverlayRef } from '@core/classes/modal-overlay.ref';
import { AppError } from '@core/models/error.model';
import { LanguageService } from '@i18n/services/language.service';
import { loadingOverlay } from '@shared/utils/loading-overlay';
import { DialogComponent } from 'src/app/modals/dialog/dialog.component';
import { ToastComponent } from 'src/app/modals/toast/toast.component';
import { ConfigService } from './config.service';

type ModalPosition = 'full' | 'center' | 'right' | 'life-cicle' | 'toast';
interface ModalConfig {
  hasBackdrop: boolean;
  position: ModalPosition;
}

@Injectable({
  providedIn: 'root'
})
export class UiService {
  dialogRef: ModalOverlayRef | null = null;

  constructor(
    private injector: Injector,
    private overlay: Overlay,
    private languageService: LanguageService,
    private configService: ConfigService
  ) {}

  openModal<T>(component: ComponentType<T>, config: ModalConfig, configModalInputs?: any) {
    const overlayRef = this.createOverlay(config);
    const dialogRef = new ModalOverlayRef(overlayRef);
    this.attachModalContainer<T>(overlayRef, dialogRef, component, configModalInputs);
    this.dialogRef = dialogRef;
    return dialogRef;
  }

  presentLoading(message?: string, forceFullScreen?: boolean) {
    loadingOverlay.present(message, forceFullScreen);
  }

  dismissLoading() {
    loadingOverlay.dismiss();
  }

  openErrorModal(error?: Error | AppError | any) {
    let errorCode = 'ERROR.DEFAULT';
    if (error && error instanceof AppError) {
      errorCode = error.code;
    }
    const errorText= error && error instanceof Error;
    // Translations are retrieved for each field since interpolation only works on strings
    const errorTranslationTitle = this.languageService.instant(`${errorCode}.TITLE`, this.configService.config);
    const errorTranslationText = errorText? error.message.replace(/^Error:\s*/, "")! :this.languageService.instant(`${errorCode}.TEXT`, this.configService.config);
    const errorTranslationButton = this.languageService.instant(`${errorCode}.BUTTON`, this.configService.config);
    this.openModal(
      DialogComponent,
      {
        hasBackdrop: true,
        position: 'center'
      },
      {
        type: 'warning',
        title: errorTranslationTitle,
        text: errorTranslationText,
        successAction: errorTranslationButton,
        cancelAction: ''
      }
    );
  }

  openToast(message: string, duration: number) {
    this.openModal(
      ToastComponent,
      {
        hasBackdrop: false,
        position: 'toast'
      },
      {
        message,
        duration
      }
    );
  }

  private createOverlay(config: ModalConfig) {
    const overlayConfig = this.getOverlayConfig(config);
    const overlayRef = this.overlay.create(overlayConfig);
    if (config.hasBackdrop) {
      overlayRef.backdropClick().subscribe({
        next: _ => overlayRef.dispose()
      });
    }
    return overlayRef;
  }

  private getOverlayConfig(config: ModalConfig): OverlayConfig {
    let positionStrategy;
    let panelClass;
    switch (config.position) {
      case 'full':
        positionStrategy = this.overlay.position().global().left('0px').top('0px');
        panelClass = 'full-panel';
        break;
      case 'center':
        positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();
        break;
      case 'toast':
        positionStrategy = this.overlay.position().global().centerHorizontally();
        panelClass = 'toast-panel';
        break;
      case 'right':
        positionStrategy = this.overlay.position().global().right('0px').top('0px');
        panelClass = 'right-panel';
        break;
      case 'life-cicle':
        positionStrategy = this.overlay.position().global().centerHorizontally().top('0px');
        panelClass = 'full-panel';
        break;
    }
    return new OverlayConfig({
      backdropClass: 'dark-backdrop',
      hasBackdrop: config.hasBackdrop,
      height: config.position === 'center' ? 'auto' : '100%',
      panelClass,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      disposeOnNavigation: true
    });
  }

  private attachModalContainer<T>(
    overlayRef: OverlayRef,
    modalRef: ModalOverlayRef,
    component: ComponentType<T>,
    configModalInputs?: any
  ) {
    const injector = this.createInjector(modalRef, configModalInputs);
    const containerPortal = new ComponentPortal(component, null, injector);
    const containerRef: ComponentRef<T> = overlayRef.attach(containerPortal);
    return containerRef.instance;
  }

  private createInjector(modalRef: ModalOverlayRef, configModalInputs?: any): Injector {
    return Injector.create({
      providers: [
        { provide: ModalOverlayRef, useValue: modalRef },
        { provide: CONFIG_MODAL_INPUTS_TOKEN, useValue: configModalInputs }
      ],
      parent: this.injector
    });
  }
}
